/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { FormattedNumber } from 'js/lib/coursera.react-intl';

import { Typography2 } from '@coursera/cds-core';
import { ContextualHelpIcon } from '@coursera/cds-icons';

import { SubmissionStatusEnum } from 'bundles/grading/api/types';
import type { GradebookEntry } from 'bundles/grading/api/types';
import TooltipOverlay from 'bundles/grading/components/common/TooltipOverlay';
import GradeOverride from 'bundles/grading/components/staff-graded-assignments/grade-override/GradeOverride';
import GradeVisibility from 'bundles/grading/components/staff-graded-assignments/right-panel/GradeVisibility';
import { getGradeTooltipText } from 'bundles/grading/utils/gradeUtils';
import { isGraded } from 'bundles/grading/utils/submissionUtils';
import { isNullOrUndefined } from 'bundles/grading/utils/typeGuardUtils';
import { truncToPlace } from 'bundles/primitive-formatting';

import _t from 'i18n!nls/grading';

type Props = {
  submitterId: string;
  submissionStatus?: SubmissionStatusEnum | null;
  rawScore: number | null | undefined;
  maxScore: number | null | undefined;
  gradebookEntry: GradebookEntry | null | undefined;
};

const styles = {
  title: () => css`
    margin-right: var(--cds-spacing-100);
  `,
  gradeContainer: () => css`
    margin: 0 var(--cds-spacing-100) 0 var(--cds-spacing-300);
  `,
  gradePercentage: () => css`
    display: inline-flex;
    align-items: center;
  `,
  overrideIcon: () => css`
    margin-left: var(--cds-spacing-50);
    color: var(--cds-color-neutral-primary-weak);
  `,
};

// Component for the assignment grade summary in right panel
const AssignmentGradeSummary: React.FC<Props> = ({
  submitterId,
  submissionStatus,
  rawScore,
  maxScore,
  gradebookEntry,
}) => {
  return (
    <div data-testid="assignment-grade-summary">
      <Typography2 variant="subtitleMedium" css={styles.title} component="span">
        {_t('Assignment grade:')}
      </Typography2>
      <span aria-label={_t('Score')}>
        <Typography2 variant="subtitleMedium" color="supportText" component="span" dir="ltr">
          <TooltipOverlay id="EarnedPointsTooltip" text={_t('Earned points')}>
            <Typography2 variant="subtitleMedium" color="body" component="span" data-testid="raw-score">
              {submissionStatus === SubmissionStatusEnum.ReadyForGrading || isNullOrUndefined(rawScore) ? (
                '--'
              ) : (
                <FormattedNumber value={rawScore} maximumFractionDigits={3} />
              )}
            </Typography2>
          </TooltipOverlay>
          {' / '}
          <TooltipOverlay id="TotalPointsTooltip" text={_t('Total points')}>
            <Typography2 variant="subtitleMedium" color="inherit" component="span">
              {isNullOrUndefined(maxScore) ? '--' : <FormattedNumber value={maxScore} maximumFractionDigits={3} />}
            </Typography2>
          </TooltipOverlay>
        </Typography2>
      </span>
      <Typography2
        aria-label={_t('Grade')}
        variant="subtitleMedium"
        dir="ltr"
        component="span"
        css={styles.gradeContainer}
      >
        {/* Grade should only be shown if submission is graded, or if there is an override applied prior to grading completion */}
        {gradebookEntry !== null &&
        gradebookEntry?.liveOutcome &&
        ((submissionStatus && isGraded(submissionStatus)) || gradebookEntry?.overrideOutcome !== null) ? (
          <TooltipOverlay id="GradeTooltip" text={getGradeTooltipText(gradebookEntry)}>
            <span data-testid="grade" css={styles.gradePercentage}>
              <FormattedNumber
                // eslint-disable-next-line react/style-prop-object
                style="percent"
                value={truncToPlace(gradebookEntry.liveOutcome.grade, 4)}
                maximumFractionDigits={2}
              />
              {gradebookEntry?.overrideOutcome && <ContextualHelpIcon size="medium" css={styles.overrideIcon} />}
            </span>
          </TooltipOverlay>
        ) : (
          <TooltipOverlay id="GradePendingTooltip" text={_t('Final grade pending')}>
            --%
          </TooltipOverlay>
        )}
      </Typography2>
      <GradeOverride key={`grade-override-${submitterId}`} />
      <GradeVisibility />
    </div>
  );
};

export default AssignmentGradeSummary;
